/**
 * Formats a date and time into ISO 8601 format with timezone
 * @param date - Date in YYYY-MM-DD format
 * @param time - Time in HH:MM format
 * @returns Formatted date-time string in YYYY-MM-DDTHH:MM:00Z format
 * @throws Error if date or time format is invalid
 */
export function formatScheduledTime(date: string, time: string): string {
  if (!date || !time) {
    throw new Error('Date and time are required');
  }

  // Validate date format (YYYY-MM-DD)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) {
    throw new Error('Invalid date format. Expected YYYY-MM-DD');
  }

  // Validate time format (HH:MM)
  const timeRegex = /^\d{2}:\d{2}$/;
  if (!timeRegex.test(time)) {
    throw new Error('Invalid time format. Expected HH:MM');
  }

  // Simply concatenate without timezone conversion
  // Format: YYYY-MM-DDTHH:MM:00Z
  return `${date}T${time}:00Z`;
}

/**
 * Formats a date string for display based on language
 * @param dateStr - Date string in various formats (YYYY-MM-DD, MM/DD/YYYY, DD-MM-YYYY)
 * @param language - Language code ('fr' or 'en')
 * @returns Formatted date string (DD/MM/YYYY for French, YYYY-MM-DD for English)
 */
export function formatAppointmentDate(
  dateStr: string | null | undefined,
  language: string
): string {
  if (!dateStr) {
    return 'N/A';
  }

  // Try to parse different date formats
  let year: string, month: string, day: string;

  // Check if date is in YYYY-MM-DD format
  if (dateStr.includes('-') && dateStr.split('-').length === 3) {
    const parts = dateStr.split('-');
    if (parts[0].length === 4) {
      // Format: YYYY-MM-DD
      [year, month, day] = parts;
    } else {
      // Format: DD-MM-YYYY (fallback)
      [day, month, year] = parts;
    }
  }
  // Check if date is in MM/DD/YYYY or DD/MM/YYYY format
  else if (dateStr.includes('/') && dateStr.split('/').length === 3) {
    const parts = dateStr.split('/');
    // Assume MM/DD/YYYY (common US format)
    [month, day, year] = parts;
  } else {
    return dateStr; // Return as-is if format is unknown
  }

  if (language === 'fr') {
    return `${day}/${month}/${year}`;
  } else {
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`; // YYYY-MM-DD
  }
}

/**
 * Formats a time string for display based on language
 * @param timeStr - Time string in HH:MM or HH:MM:SS format (may include AM/PM)
 * @param language - Language code ('fr' or 'en')
 * @returns Formatted time string (14h30 for French, 2:30 PM for English)
 */
export function formatAppointmentTime(
  timeStr: string | null | undefined,
  language: string
): string {
  if (!timeStr) {
    return 'N/A';
  }

  // Remove any existing AM/PM suffixes first
  const cleanTime = timeStr.replaceAll(/\s*(AM|PM|am|pm)\s*/g, '').trim();

  // Handle time format HH:MM or HH:MM:SS
  const timeParts = cleanTime.split(':');
  if (timeParts.length < 2) {
    return timeStr;
  }

  const hours = timeParts[0];
  const minutes = timeParts[1];
  const hour = Number.parseInt(hours, 10);

  if (language === 'fr') {
    // Format français: 14h30, 8h00
    return `${hour}h${minutes}`;
  } else {
    // Format anglais: 2:30 PM, 8:00 AM
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${period}`;
  }
}
